import {
  Calendar,
  Clock,
  Inbox,
  Pencil,
  UserX,
  UserCheck,
  Trash2,
  Sparkles,
} from "lucide-react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import swal from "../../utils/swal";
import { Image } from "../../components/ui";
import { UPLOADS_URL } from "../../config/constants";
import { useUpdateActivity } from "../../query/activityQuery";
import { DetailCoverShowcase } from "../../components/DetailCoverHero";
import { getActivityImageSrc } from "./activityHelpers";
import "../teachers/teacherDetails.css";
import "./activities.css";

function buildStatusFormData(activity, status) {
  const formObject = new FormData();
  formObject.append("title", activity.title || "");
  formObject.append("description", activity.description || "");
  formObject.append("status", status);
  return formObject;
}

export default function ActivityProfile({ activity, onDelete }) {
  const navigate = useNavigate();
  const { mutate: updateActivity, isPending: isUpdatingStatus } = useUpdateActivity();

  const title = activity?.title || "Activity";
  const isActive = activity?.status === "ACTIVE";
  const statusText = isActive ? "Active" : "Inactive";
  const imageSrc = getActivityImageSrc(activity, UPLOADS_URL);

  const handleStatus = () => {
    const nextStatus = isActive ? "INACTIVE" : "ACTIVE";
    swal({
      title: "Are you sure?",
      text: `Do you want to ${nextStatus === "ACTIVE" ? "activate" : "deactivate"} this activity?`,
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then((isConfirm) => {
      if (!isConfirm) return;
      updateActivity(
        { id: activity._id, data: buildStatusFormData(activity, nextStatus) },
        {
          onSuccess: () => swal("Success", "Status updated successfully", "success"),
          onError: (error) => swal("Error", error?.message || "Failed to update status", "error"),
        }
      );
    });
  };

  return (
    <div className="td-page act-page-single">
      <div className="td-main">
        <article className="td-card act-detail-card">
          <DetailCoverShowcase
            coverKey={activity?._id}
            backLabel="Back to activities"
            onBack={() => navigate("/activities")}
          />

          <div className="act-detail-body">
            <div className="act-detail-layout">
              <div className="act-detail-copy">
                <div className="act-detail-head">
                  <div className="act-detail-head-copy">
                    <span className="act-detail-kicker">
                      <Sparkles size={14} />
                      Post category
                    </span>
                    <div className="act-detail-title-row">
                      <h2>{title}</h2>
                      <span className={`td-status is-${(activity?.status || "inactive").toLowerCase()}`}>
                        {statusText}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="act-detail-meta">
                  <span>
                    <i className="is-orange">
                      <Calendar size={14} />
                    </i>
                    {activity?.createdAt
                      ? `Created ${dayjs(activity.createdAt).format("MMMM D, YYYY")}`
                      : "Created date not set"}
                  </span>
                  <span>
                    <i className="is-orange">
                      <Clock size={14} />
                    </i>
                    {activity?.updatedAt
                      ? `Updated ${dayjs(activity.updatedAt).format("MMMM D, YYYY")}`
                      : "Not updated yet"}
                  </span>
                </div>

                <section className="act-detail-section">
                  <h3>About this category</h3>
                  {activity?.description ? (
                    <p>{activity.description}</p>
                  ) : (
                    <div className="act-detail-empty">
                      <Inbox size={22} />
                      <span>No description provided</span>
                    </div>
                  )}
                </section>
              </div>

              <figure className="act-detail-visual">
                {imageSrc ? (
                  <>
                    <Image className="act-detail-poster" src={imageSrc} alt={title} />
                    <figcaption>Click image to preview</figcaption>
                  </>
                ) : (
                  <div className="act-detail-poster is-empty">
                    <Sparkles size={36} strokeWidth={1.6} />
                    <span>No illustration</span>
                  </div>
                )}
              </figure>
            </div>
          </div>
        </article>

        <div className="td-actions act-detail-actions">
          <button
            type="button"
            className="td-action is-primary"
            onClick={() => navigate(`/activities/edit/${activity._id}`)}
          >
            <Pencil size={15} />
            Edit details
          </button>
          <button
            type="button"
            className="td-action"
            disabled={isUpdatingStatus}
            onClick={handleStatus}
          >
            {isActive ? <UserX size={15} /> : <UserCheck size={15} />}
            {isActive ? "Deactivate" : "Activate"}
          </button>
          <button type="button" className="td-action is-danger" onClick={onDelete}>
            <Trash2 size={15} />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
